import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Select, MenuItem, FormControl, InputLabel, CircularProgress,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert,
    IconButton, Paper, Card, CardContent, Divider, Tabs, Tab, Tooltip,
    Table, TableHead, TableRow, TableCell, TableBody, TablePagination
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
    fetchOrganizations,
    fetchClipsByOrganization,
    fetchSummaryForClip,
    fetchSummaryStatus,
    generateSummary,
    updateSummary,
    deleteSummary,
    fetchAIAccounts,
    createAIAccount,
    updateAIAccount,
    deleteAIAccount,
    enableAIAccount,
    disableAIAccount,
    resetAIAccountStatus,
    fetchAIAccountLogs,
    fetchAIAccountStats,
} from '../api/apiService';
import { formatDateTime, formatDateTimeLocal } from '../utils/formatDateTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import KeyIcon from '@mui/icons-material/Key';

const normalizeSummaryStatus = (status) => {
    if (status === 'completed') return 'yes';
    if (status === 'processing') return 'processing';
    if (status === 'failed') return 'failed';
    return 'no';
};

// AI Key 状态映射
const AI_KEY_STATUS_MAP = {
    active:    { label: '正常',    color: 'success' },
    cooldown:  { label: '冷却中',  color: 'warning' },
    exhausted: { label: '额度耗尽', color: 'error' },
    invalid:   { label: '密钥无效', color: 'error' },
    disabled:  { label: '已停用',  color: 'default' },
};

// AI Key 账号表单初始值
const ACCOUNT_FORM_INIT = {
    provider: 'siliconflow',
    name: '',
    scene: 'summary',
    baseUrl: 'https://api.siliconflow.cn/v1',
    apiKey: '',
    model: 'deepseek-ai/DeepSeek-V3.2',
    priority: 1,
    weight: 1,
    remark: '',
};

// 格式化毫秒为可读时间
const formatTime = (ms) => {
    if (ms === undefined || ms === null) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const parseBalance = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

const getBalanceChipColor = (provider, balanceValue) => {
    if (provider !== 'siliconflow' || balanceValue === null) return 'default';
    if (balanceValue >= 10) return 'success';
    if (balanceValue >= 1) return 'warning';
    return 'error';
};


const SummaryPage = () => {
    // ── Tab ──────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState(0);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [clips, setClips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // 分页状态
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
    const [totalRows, setTotalRows] = useState(0);

    // AI总结状态: { clipId: 'yes' | 'no' | 'processing' | 'failed' }
    const [summaryStatus, setSummaryStatus] = useState({});
    const pollingRef = useRef({}); // 轮询定时器: { clipId: intervalId }

    // 编辑弹窗状态
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentClip, setCurrentClip] = useState(null);
    const [currentSummary, setCurrentSummary] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState({ msg: '', type: '' });

    // 删除确认弹窗
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [clipToDelete, setClipToDelete] = useState(null);

    // 生成状态提示
    const [generateStatus, setGenerateStatus] = useState({ clipId: null, msg: '', type: '' });

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const data = await fetchOrganizations();
                setOrganizations(data);
                if (data.length > 0) {
                    setSelectedOrg(data[0].id);
                }
            } catch (err) { setError('无法加载社团列表'); }
        };
        fetchOrgs();

        // 清理轮询定时器
        return () => {
            Object.values(pollingRef.current).forEach(intervalId => clearInterval(intervalId));
        };
    }, []);

    // 开始轮询某个 clip 的生成状态
    const startPolling = useCallback((clipId) => {
        if (pollingRef.current[clipId]) return; // 已在轮询

        const intervalId = setInterval(async () => {
            try {
                const status = await fetchSummaryStatus(clipId);
                if (status.status === 'completed') {
                    // 生成完成
                    setSummaryStatus(prev => ({ ...prev, [clipId]: 'yes' }));
                    setGenerateStatus({ clipId, msg: 'AI 总结生成完成！', type: 'success' });
                    clearInterval(pollingRef.current[clipId]);
                    delete pollingRef.current[clipId];
                    // 3秒后清除提示
                    setTimeout(() => {
                        setGenerateStatus(prev => prev.clipId === clipId ? { clipId: null, msg: '', type: '' } : prev);
                    }, 3000);
                } else if (status.status === 'not_started') {
                    // 可能生成失败了
                    setSummaryStatus(prev => ({ ...prev, [clipId]: 'no' }));
                    setGenerateStatus({ clipId, msg: 'AI 总结生成可能失败，请重试', type: 'error' });
                    clearInterval(pollingRef.current[clipId]);
                    delete pollingRef.current[clipId];
                }
                // 如果还是 processing，继续轮询
            } catch (e) {
                // 出错时停止轮询
                clearInterval(pollingRef.current[clipId]);
                delete pollingRef.current[clipId];
            }
        }, 5000); // 每5秒检查一次

        pollingRef.current[clipId] = intervalId;
    }, []);

    // 加载 clips 列表
    const refreshData = useCallback(async () => {
        if (!selectedOrg) return;
        setLoading(true);
        setError('');
        try {
            const clipsResponse = await fetchClipsByOrganization(selectedOrg, {
                page: paginationModel.page + 1,
                size: paginationModel.pageSize
            });
            const clipsData = clipsResponse.clips || [];
            setClips(clipsData);
            setTotalRows(clipsResponse.total || 0);

            const nextStatus = {};
            clipsData.forEach(clip => {
                nextStatus[clip.id] = normalizeSummaryStatus(clip.summaryStatus);
                if (clip.summaryStatus === 'processing') {
                    startPolling(clip.id);
                }
            });
            setSummaryStatus(nextStatus);
        } catch (err) {
            setError(`加载数据失败: ${err.message}`);
            setClips([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [selectedOrg, paginationModel, startPolling]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    // 切换社团时重置分页和清理轮询
    useEffect(() => {
        Object.values(pollingRef.current).forEach(intervalId => clearInterval(intervalId));
        pollingRef.current = {};
        setSummaryStatus({});
        setPaginationModel({ page: 0, pageSize: 20 });
    }, [selectedOrg]);

    // 获取状态显示
    const getSummaryStatusDisplay = (clipId) => {
        const status = summaryStatus[clipId];
        if (status === 'processing') {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="primary">生成中...</Typography>
                </Box>
            );
        }
        if (status === 'yes') {
            return <Chip icon={<CheckCircleIcon />} label="已生成" color="success" size="small" variant="outlined" sx={{ borderRadius: 1 }} />;
        }
        if (status === 'failed') {
            return <Chip icon={<CancelIcon />} label="生成失败" color="error" size="small" variant="outlined" sx={{ borderRadius: 1 }} />;
        }
        return <Chip icon={<CancelIcon />} label="未生成" color="default" size="small" variant="outlined" sx={{ borderRadius: 1 }} />;
    };

    // 生成 AI 总结
    const handleGenerate = async (clip) => {
        const status = summaryStatus[clip.id];
        if (status === 'processing') {
            setGenerateStatus({ clipId: clip.id, msg: '该视频正在生成中，请稍候...', type: 'warning' });
            return;
        }

        setSummaryStatus(prev => ({ ...prev, [clip.id]: 'processing' }));
        setGenerateStatus({ clipId: clip.id, msg: '正在提交生成请求...', type: 'info' });

        try {
            const result = await generateSummary(clip.id);
            if (result.status === 'processing') {
                setGenerateStatus({ clipId: clip.id, msg: 'AI 正在生成摘要，预计需要1-5分钟...', type: 'info' });
                startPolling(clip.id);
            } else if (result.status === 'success') {
                setSummaryStatus(prev => ({ ...prev, [clip.id]: 'yes' }));
                setGenerateStatus({ clipId: clip.id, msg: 'AI 总结生成成功！', type: 'success' });
                setTimeout(() => {
                    setGenerateStatus(prev => prev.clipId === clip.id ? { clipId: null, msg: '', type: '' } : prev);
                }, 3000);
            }
        } catch (err) {
            setSummaryStatus(prev => ({ ...prev, [clip.id]: 'no' }));
            setGenerateStatus({ clipId: clip.id, msg: `生成失败: ${err.message}`, type: 'error' });
        }
    };

    // 打开编辑弹窗
    const handleOpenEditDialog = async (clip) => {
        setCurrentClip(clip);
        setEditLoading(true);
        setSubmissionStatus({ msg: '', type: '' });
        setEditDialogOpen(true);

        try {
            const summary = await fetchSummaryForClip(clip.id);
            setCurrentSummary(summary);
        } catch (err) {
            setSubmissionStatus({ msg: `加载失败: ${err.message}`, type: 'error' });
            setCurrentSummary(null);
        } finally {
            setEditLoading(false);
        }
    };

    // 更新话题
    const handleTopicChange = (index, field, value) => {
        setCurrentSummary(prev => {
            const newTopics = [...prev.topics];
            if (field === 'startTime' || field === 'endTime') {
                // 将时间字符串转换为毫秒
                const parts = value.split(':');
                if (parts.length === 3) {
                    const [h, m, s] = parts.map(Number);
                    newTopics[index][field] = ((h * 3600) + (m * 60) + s) * 1000;
                }
            } else {
                newTopics[index][field] = value;
            }
            return { ...prev, topics: newTopics };
        });
    };

    // 添加话题
    const handleAddTopic = () => {
        setCurrentSummary(prev => ({
            ...prev,
            topics: [...prev.topics, { title: '', startTime: 0, endTime: 0, summary: '' }]
        }));
    };

    // 删除话题
    const handleRemoveTopic = (index) => {
        setCurrentSummary(prev => ({
            ...prev,
            topics: prev.topics.filter((_, i) => i !== index)
        }));
    };

    // 更新关键词
    const handleAddKeyword = (e) => {
        if (e.key === 'Enter' && e.target.value.trim()) {
            e.preventDefault();
            const newKeyword = e.target.value.trim();
            if (!currentSummary.keywords.includes(newKeyword)) {
                setCurrentSummary(prev => ({
                    ...prev,
                    keywords: [...(prev.keywords || []), newKeyword]
                }));
            }
            e.target.value = '';
        }
    };

    const handleDeleteKeyword = (keywordToDelete) => {
        setCurrentSummary(prev => ({
            ...prev,
            keywords: prev.keywords.filter(k => k !== keywordToDelete)
        }));
    };

    // 编辑单个关键词
    const [editingKeywordIndex, setEditingKeywordIndex] = useState(null);
    const [editingKeywordValue, setEditingKeywordValue] = useState('');

    const handleStartEditKeyword = (index) => {
        setEditingKeywordIndex(index);
        setEditingKeywordValue(currentSummary.keywords[index]);
    };

    const handleSaveKeyword = () => {
        if (editingKeywordValue.trim() && editingKeywordIndex !== null) {
            const newValue = editingKeywordValue.trim();
            // 检查是否与其他关键词重复
            const isDuplicate = currentSummary.keywords.some(
                (k, i) => i !== editingKeywordIndex && k === newValue
            );
            if (!isDuplicate) {
                setCurrentSummary(prev => ({
                    ...prev,
                    keywords: prev.keywords.map((k, i) => 
                        i === editingKeywordIndex ? newValue : k
                    )
                }));
            }
        }
        setEditingKeywordIndex(null);
        setEditingKeywordValue('');
    };

    const handleCancelEditKeyword = () => {
        setEditingKeywordIndex(null);
        setEditingKeywordValue('');
    };

    const handleKeywordKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveKeyword();
        } else if (e.key === 'Escape') {
            handleCancelEditKeyword();
        }
    };

    // 更新整体摘要
    const handleOverallSummaryChange = (value) => {
        setCurrentSummary(prev => ({ ...prev, overallSummary: value }));
    };

    // 关闭编辑弹窗
    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingKeywordIndex(null);
        setEditingKeywordValue('');
    };

    // 提交编辑
    const handleConfirmEdit = async () => {
        if (!currentSummary) return;

        setSubmissionStatus({ msg: '保存中...', type: 'info' });
        try {
            await updateSummary(currentClip.id, {
                topics: currentSummary.topics,
                keywords: currentSummary.keywords,
                overallSummary: currentSummary.overallSummary
            });
            setSubmissionStatus({ msg: '保存成功！', type: 'success' });
            setTimeout(() => {
                handleCloseEditDialog();
            }, 1500);
        } catch (err) {
            setSubmissionStatus({ msg: `保存失败: ${err.message}`, type: 'error' });
        }
    };

    // 打开删除确认弹窗
    const handleOpenDeleteDialog = (clip) => {
        setClipToDelete(clip);
        setDeleteDialogOpen(true);
    };

    // 确认删除
    const handleConfirmDelete = async () => {
        try {
            await deleteSummary(clipToDelete.id);
            setSummaryStatus(prev => ({ ...prev, [clipToDelete.id]: 'no' }));
            setDeleteDialogOpen(false);
            setClipToDelete(null);
        } catch (err) {
            alert(`删除失败: ${err.message}`);
        }
    };

    // ── AI Key 管理状态 ──────────────────────────────────────────────
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountsError, setAccountsError] = useState('');

    // 账号新增/编辑弹窗
    const [accountDialogOpen, setAccountDialogOpen] = useState(false);
    const [isEditingAccount, setIsEditingAccount] = useState(false);
    const [currentAccount, setCurrentAccount] = useState(ACCOUNT_FORM_INIT);
    const [accountSaving, setAccountSaving] = useState(false);
    const [accountSaveMsg, setAccountSaveMsg] = useState({ msg: '', type: '' });

    // 删除账号确认弹窗
    const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);

    // 日志抽屉
    const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
    const [logsAccount, setLogsAccount] = useState(null);
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logsPage, setLogsPage] = useState(0);
    const [logsRowsPerPage, setLogsRowsPerPage] = useState(20);

    // 统计抽屉
    const [statsDrawerOpen, setStatsDrawerOpen] = useState(false);
    const [stats, setStats] = useState([]);
    const [statsLoading, setStatsLoading] = useState(false);

    // 加载 AI Key 账号列表
    const loadAccounts = useCallback(async () => {
        setAccountsLoading(true);
        setAccountsError('');
        try {
            const data = await fetchAIAccounts();
            setAccounts(data);
        } catch (err) {
            setAccountsError(err.message);
        } finally {
            setAccountsLoading(false);
        }
    }, []);

    // 切换到 AI Key Tab 时自动加载
    useEffect(() => {
        if (activeTab === 1) {
            loadAccounts();
        }
    }, [activeTab, loadAccounts]);

    // 新增弹窗
    const handleOpenCreateAccountDialog = () => {
        setIsEditingAccount(false);
        setCurrentAccount(ACCOUNT_FORM_INIT);
        setAccountSaveMsg({ msg: '', type: '' });
        setAccountDialogOpen(true);
    };

    // 编辑弹窗
    const handleOpenEditAccountDialog = (account) => {
        setIsEditingAccount(true);
        setCurrentAccount({
            id: account.id,
            provider: account.provider || 'siliconflow',
            name: account.name || '',
            scene: account.scene || 'summary',
            baseUrl: account.baseUrl || '',
            apiKey: '', // 不回填明文，留空表示不更换
            model: account.model || '',
            priority: account.priority ?? 1,
            weight: account.weight ?? 1,
            remark: account.remark || '',
        });
        setAccountSaveMsg({ msg: '', type: '' });
        setAccountDialogOpen(true);
    };

    // 保存账号
    const handleSaveAccount = async () => {
        const { id, provider, name, scene, baseUrl, apiKey, model, priority, weight, remark } = currentAccount;
        if (!name.trim()) { setAccountSaveMsg({ msg: '请填写账号名称', type: 'error' }); return; }
        if (!baseUrl.trim()) { setAccountSaveMsg({ msg: '请填写 Base URL', type: 'error' }); return; }
        if (!model.trim()) { setAccountSaveMsg({ msg: '请填写模型', type: 'error' }); return; }
        const priorityNum = Number(priority);
        const weightNum = Number(weight);
        if (!Number.isInteger(priorityNum) || priorityNum < 1) { setAccountSaveMsg({ msg: '优先级须为正整数', type: 'error' }); return; }
        if (!Number.isInteger(weightNum) || weightNum < 1) { setAccountSaveMsg({ msg: '权重须为正整数且 ≥ 1', type: 'error' }); return; }
        if (!isEditingAccount && !apiKey.trim()) { setAccountSaveMsg({ msg: '新增时 API Key 为必填', type: 'error' }); return; }

        setAccountSaving(true);
        setAccountSaveMsg({ msg: '', type: '' });
        try {
            const payload = { provider, name, scene, baseUrl, model, priority: priorityNum, weight: weightNum, remark };
            if (apiKey.trim()) payload.apiKey = apiKey;
            if (isEditingAccount) {
                await updateAIAccount(id, payload);
            } else {
                await createAIAccount(payload);
            }
            setAccountSaveMsg({ msg: '保存成功！', type: 'success' });
            setTimeout(() => {
                setAccountDialogOpen(false);
                loadAccounts();
            }, 800);
        } catch (err) {
            setAccountSaveMsg({ msg: `保存失败: ${err.message}`, type: 'error' });
        } finally {
            setAccountSaving(false);
        }
    };

    // 删除账号
    const handleOpenDeleteAccount = (account) => {
        setAccountToDelete(account);
        setDeleteAccountDialogOpen(true);
    };
    const handleConfirmDeleteAccount = async () => {
        try {
            await deleteAIAccount(accountToDelete.id);
            setDeleteAccountDialogOpen(false);
            setAccountToDelete(null);
            loadAccounts();
        } catch (err) {
            alert(`删除失败: ${err.message}`);
        }
    };

    // 启用 / 停用
    const handleToggleAccount = async (account) => {
        try {
            if (account.enabled) {
                await disableAIAccount(account.id);
            } else {
                await enableAIAccount(account.id);
            }
            loadAccounts();
        } catch (err) {
            alert(`操作失败: ${err.message}`);
        }
    };

    // 重置状态
    const handleResetAccount = async (account) => {
        if (!window.confirm(`确定要重置账号「${account.name}」的状态吗？`)) return;
        try {
            await resetAIAccountStatus(account.id);
            loadAccounts();
        } catch (err) {
            alert(`重置失败: ${err.message}`);
        }
    };

    // 打开日志抽屉
    const handleOpenLogs = async (account) => {
        setLogsAccount(account);
        setLogsPage(0);
        setLogsDrawerOpen(true);
        setLogsLoading(true);
        setLogs([]);
        try {
            const data = await fetchAIAccountLogs(account.id, { limit: logsRowsPerPage, offset: 0 });
            setLogs(data);
        } catch (err) {
            alert(`加载日志失败: ${err.message}`);
        } finally {
            setLogsLoading(false);
        }
    };

    // 日志翻页
    const handleLogsPageChange = async (_, newPage) => {
        setLogsPage(newPage);
        setLogsLoading(true);
        try {
            const data = await fetchAIAccountLogs(logsAccount.id, {
                limit: logsRowsPerPage,
                offset: newPage * logsRowsPerPage,
            });
            setLogs(data);
        } catch (err) {
            alert(`加载日志失败: ${err.message}`);
        } finally {
            setLogsLoading(false);
        }
    };

    const handleLogsRowsPerPageChange = async (e) => {
        const newSize = parseInt(e.target.value, 10);
        setLogsRowsPerPage(newSize);
        setLogsPage(0);
        setLogsLoading(true);
        try {
            const data = await fetchAIAccountLogs(logsAccount.id, { limit: newSize, offset: 0 });
            setLogs(data);
        } catch (err) {
            alert(`加载日志失败: ${err.message}`);
        } finally {
            setLogsLoading(false);
        }
    };

    // 打开统计抽屉
    const handleOpenStats = async () => {
        setStatsDrawerOpen(true);
        setStatsLoading(true);
        setStats([]);
        try {
            const data = await fetchAIAccountStats();
            setStats(data);
        } catch (err) {
            alert(`加载统计失败: ${err.message}`);
        } finally {
            setStatsLoading(false);
        }
    };

    // AI Key 表格列定义
    const accountColumns = [
        { field: 'id', headerName: 'ID', width: 60 },
        { field: 'name', headerName: '名称', width: 140, flex: 1 },
        { field: 'provider', headerName: 'Provider', width: 100 },
        { field: 'scene', headerName: 'Scene', width: 90 },
        { field: 'model', headerName: '模型', width: 200 },
        { field: 'apiKeyMasked', headerName: 'API Key', width: 160 },
        { field: 'baseUrl', headerName: 'Base URL', width: 220 },
        {
            field: 'balance',
            headerName: '余额',
            width: 110,
            renderCell: ({ row }) => {
                const rawBalance = row.balance;
                const display = rawBalance !== undefined && rawBalance !== null && rawBalance !== '' ? String(rawBalance) : '--';
                const balanceNum = parseBalance(rawBalance);
                const chipColor = getBalanceChipColor(row.provider, balanceNum);
                return <Chip label={display} color={chipColor} size="small" variant="outlined" sx={{ borderRadius: 1 }} />;
            },
        },
        {
            field: 'balanceUpdatedAt',
            headerName: '余额更新时间',
            width: 170,
            valueFormatter: (value) => formatDateTimeLocal(value),
        },
        {
            field: 'status',
            headerName: '状态',
            width: 110,
            renderCell: ({ value }) => {
                const s = AI_KEY_STATUS_MAP[value] || { label: value || '-', color: 'default' };
                return <Chip label={s.label} color={s.color} size="small" variant="outlined" sx={{ borderRadius: 1 }} />;
            },
        },
        {
            field: 'enabled',
            headerName: '启用',
            width: 70,
            renderCell: ({ value }) => (
                <Chip label={value ? '是' : '否'} color={value ? 'success' : 'default'} size="small" variant="outlined" sx={{ borderRadius: 1 }} />
            ),
        },
        { field: 'priority', headerName: '优先级', width: 80, type: 'number' },
        { field: 'weight', headerName: '权重', width: 70, type: 'number' },
        { field: 'consecutiveFailures', headerName: '连续失败', width: 90, type: 'number' },
        { field: 'lastErrorCode', headerName: '最近错误码', width: 120 },
        {
            field: 'lastUsedAt',
            headerName: '最近使用',
            width: 160,
            valueFormatter: (value) => formatDateTime(value),
        },
        { field: 'remark', headerName: '备注', width: 120 },
        {
            field: 'actions',
            headerName: '操作',
            width: 230,
            sortable: false,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Tooltip title="编辑">
                        <IconButton size="small" color="primary" onClick={() => handleOpenEditAccountDialog(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={row.enabled ? '停用' : '启用'}>
                        <IconButton size="small" color={row.enabled ? 'warning' : 'success'} onClick={() => handleToggleAccount(row)}>
                            {row.enabled ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="重置状态">
                        <IconButton size="small" color="info" onClick={() => handleResetAccount(row)}>
                            <RestartAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="查看日志">
                        <IconButton size="small" onClick={() => handleOpenLogs(row)}>
                            <ListAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="删除">
                        <IconButton size="small" color="error" onClick={() => handleOpenDeleteAccount(row)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    // ── AI 总结 DataGrid 列定义（原有）───────────────────────────────
    const columns = [
        { field: 'id', headerName: 'Clip ID', width: 100 },
        { field: 'title', headerName: '标题', flex: 1, minWidth: 300 },
        { field: 'part', headerName: 'Part', width: 80 },
        {
            field: 'hasSummary',
            headerName: '有无AI总结',
            width: 150,
            renderCell: (params) => getSummaryStatusDisplay(params.row.id),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '操作',
            width: 150,
            getActions: ({ row }) => {
                const status = summaryStatus[row.id];
                
                if (status === 'processing') {
                    return [
                        <GridActionsCellItem
                            icon={<CircularProgress size={20} />}
                            label="Generating..."
                            disabled
                        />,
                    ];
                }
                
                if (status === 'yes') {
                    return [
                        <GridActionsCellItem
                            icon={<AutoAwesomeIcon color="primary" />}
                            label="Regenerate"
                            onClick={() => handleGenerate(row)}
                            title="重新生成"
                            showInMenu={false}
                        />,
                        <GridActionsCellItem
                            icon={<EditIcon color="action" />}
                            label="Edit"
                            onClick={() => handleOpenEditDialog(row)}
                            title="编辑"
                            showInMenu={false}
                        />,
                        <GridActionsCellItem
                            icon={<DeleteIcon color="error" />}
                            label="Delete"
                            onClick={() => handleOpenDeleteDialog(row)}
                            title="删除"
                            showInMenu={false}
                        />,
                    ];
                }
                
                return [
                    <GridActionsCellItem
                        icon={<AutoAwesomeIcon color="primary" />}
                        label="Generate"
                        onClick={() => handleGenerate(row)}
                        title="生成AI总结"
                        showInMenu={false}
                    />,
                ];
            },
        },
    ];

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* ── 页头 ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>AI 管理</Typography>
            </Box>

            {/* ── Tab 切换 ── */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    <Tab icon={<AutoAwesomeIcon />} iconPosition="start" label="AI 总结" />
                    <Tab icon={<KeyIcon />} iconPosition="start" label="AI Key 管理" />
                </Tabs>
            </Box>

            {/* ══════════════════════ Tab 0: AI 总结 ══════════════════════ */}
            {activeTab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <FormControl sx={{ minWidth: 240 }} size="small">
                            <InputLabel id="org-select-label">选择社团</InputLabel>
                            <Select
                                labelId="org-select-label"
                                value={selectedOrg}
                                label="选择社团"
                                onChange={(e) => setSelectedOrg(e.target.value)}
                                sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
                            >
                                {organizations.map((org) => (
                                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {generateStatus.msg && (
                        <Alert severity={generateStatus.type} onClose={() => setGenerateStatus({ clipId: null, msg: '', type: '' })} sx={{ borderRadius: 2 }}>
                            {generateStatus.clipId && `Clip ${generateStatus.clipId}: `}{generateStatus.msg}
                        </Alert>
                    )}

                    {error ? <Typography color="error">{error}</Typography> : (
                        <Paper sx={{ flexGrow: 1, width: '100%', overflow: 'hidden', borderRadius: 3, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}>
                            <DataGrid
                                rows={clips}
                                columns={columns}
                                loading={loading}
                                paginationMode="server"
                                rowCount={totalRows}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[10, 20, 50]}
                                initialState={{
                                    sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
                                }}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderColor: 'divider' },
                                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' },
                                }}
                            />
                        </Paper>
                    )}
                </Box>
            )}

            {/* ══════════════════════ Tab 1: AI Key 管理 ══════════════════════ */}
            {activeTab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
                    {/* 操作栏 */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button variant="outlined" startIcon={<BarChartIcon />} onClick={handleOpenStats}>
                            查看统计
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateAccountDialog}>
                            新增账号
                        </Button>
                    </Box>

                    {accountsError && (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>{accountsError}</Alert>
                    )}

                    <Paper sx={{ flexGrow: 1, width: '100%', overflow: 'hidden', borderRadius: 3, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}>
                        <DataGrid
                            rows={accounts}
                            columns={accountColumns}
                            loading={accountsLoading}
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                            disableRowSelectionOnClick
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderColor: 'divider' },
                                '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' },
                            }}
                        />
                    </Paper>
                </Box>
            )}

            {/* ══════════════════════ 弹窗：AI 总结编辑 ══════════════════════ */}
            <Dialog
                open={editDialogOpen}
                onClose={handleCloseEditDialog}
                fullWidth
                maxWidth="lg"
                PaperProps={{ sx: { borderRadius: 3, boxShadow: '0px 10px 40px rgba(0,0,0,0.1)' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">编辑 AI 总结</Typography>
                        <Typography variant="body2" color="text.secondary">Clip ID: {currentClip?.id} - {currentClip?.title}</Typography>
                    </Box>
                    <IconButton onClick={handleCloseEditDialog} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 4, bgcolor: 'background.default' }}>
                    {editLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
                    ) : currentSummary ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* 关键词 */}
                            <Card sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                                <CardContent>
                                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="text.secondary">
                                        关键词 <Typography component="span" variant="caption" color="text.disabled">(双击编辑)</Typography>
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {currentSummary.keywords?.map((keyword, index) => (
                                            editingKeywordIndex === index ? (
                                                <TextField
                                                    key={index}
                                                    size="small"
                                                    autoFocus
                                                    value={editingKeywordValue}
                                                    onChange={(e) => setEditingKeywordValue(e.target.value)}
                                                    onKeyDown={handleKeywordKeyDown}
                                                    onBlur={handleSaveKeyword}
                                                    sx={{ width: 'auto', minWidth: 100, '& .MuiInputBase-input': { py: 0.5, px: 1, fontSize: '0.8125rem' } }}
                                                />
                                            ) : (
                                                <Chip
                                                    key={index}
                                                    label={keyword}
                                                    onDoubleClick={() => handleStartEditKeyword(index)}
                                                    onDelete={() => handleDeleteKeyword(keyword)}
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ cursor: 'pointer' }}
                                                />
                                            )
                                        ))}
                                    </Box>
                                    <TextField
                                        fullWidth
                                        placeholder="输入关键词后按回车添加"
                                        onKeyDown={handleAddKeyword}
                                        variant="outlined"
                                        size="small"
                                        sx={{ bgcolor: 'background.paper' }}
                                    />
                                </CardContent>
                            </Card>
                            {/* 整体摘要 */}
                            <Card sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
                                <CardContent>
                                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="text.secondary">整体摘要</Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={6}
                                        maxRows={12}
                                        value={currentSummary.overallSummary || ''}
                                        onChange={(e) => handleOverallSummaryChange(e.target.value)}
                                        placeholder="输入整体摘要..."
                                        variant="outlined"
                                        sx={{ bgcolor: 'background.paper' }}
                                    />
                                </CardContent>
                            </Card>
                            {/* 话题列表 */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" fontWeight="bold">话题列表 ({currentSummary.topics?.length || 0})</Typography>
                                    <Button variant="contained" size="small" onClick={handleAddTopic} startIcon={<AutoAwesomeIcon />}>添加话题</Button>
                                </Box>
                                {currentSummary.topics?.map((topic, index) => (
                                    <Card key={index} sx={{ mb: 2, borderRadius: 2, boxShadow: '0px 2px 8px rgba(0,0,0,0.05)' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Chip label={`话题 #${index + 1}`} color="primary" size="small" variant="outlined" />
                                                <IconButton size="small" color="error" onClick={() => handleRemoveTopic(index)} title="删除话题">
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <TextField fullWidth label="话题标题" value={topic.title || ''} onChange={(e) => handleTopicChange(index, 'title', e.target.value)} variant="outlined" size="small" />
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <TextField fullWidth label="开始时间" value={formatTime(topic.startTime)} onChange={(e) => handleTopicChange(index, 'startTime', e.target.value)} variant="outlined" size="small" placeholder="HH:MM:SS" InputProps={{ startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" /> }} />
                                                    <TextField fullWidth label="结束时间" value={formatTime(topic.endTime)} onChange={(e) => handleTopicChange(index, 'endTime', e.target.value)} variant="outlined" size="small" placeholder="HH:MM:SS" InputProps={{ startAdornment: <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" /> }} />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontWeight: 'bold' }}>话题摘要</Typography>
                                                    <TextField fullWidth value={topic.summary || ''} onChange={(e) => handleTopicChange(index, 'summary', e.target.value)} variant="outlined" size="small" multiline minRows={4} maxRows={8} />
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        </Box>
                    ) : (
                        <Typography color="error">无法加载 AI 总结数据</Typography>
                    )}
                    {submissionStatus.msg && (
                        <Alert severity={submissionStatus.type} sx={{ mt: 2 }}>{submissionStatus.msg}</Alert>
                    )}
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
                    <Button onClick={handleCloseEditDialog} color="inherit">取消</Button>
                    <Button onClick={handleConfirmEdit} variant="contained" disabled={!currentSummary || editLoading} startIcon={<SaveIcon />}>保存修改</Button>
                </DialogActions>
            </Dialog>

            {/* 弹窗：AI 总结删除确认 */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle>确认删除 AI 总结</DialogTitle>
                <DialogContent>
                    <Typography>您确定要删除 Clip ID: <strong>{clipToDelete?.id}</strong> 的 AI 总结吗？此操作不可撤销。</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">取消</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" disableElevation>确认删除</Button>
                </DialogActions>
            </Dialog>

            {/* ══════════════════════ 弹窗：AI Key 新增/编辑 ══════════════════════ */}
            <Dialog
                open={accountDialogOpen}
                onClose={() => { if (!accountSaving) setAccountDialogOpen(false); }}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">{isEditingAccount ? '编辑 AI Key 账号' : '新增 AI Key 账号'}</Typography>
                    <IconButton onClick={() => setAccountDialogOpen(false)} size="small" disabled={accountSaving}><CloseIcon /></IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="账号名称"
                            fullWidth
                            size="small"
                            value={currentAccount.name}
                            onChange={(e) => setCurrentAccount({ ...currentAccount, name: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Provider"
                                fullWidth
                                size="small"
                                value={currentAccount.provider}
                                onChange={(e) => setCurrentAccount({ ...currentAccount, provider: e.target.value })}
                            />
                            <TextField
                                label="Scene"
                                fullWidth
                                size="small"
                                value={currentAccount.scene}
                                onChange={(e) => setCurrentAccount({ ...currentAccount, scene: e.target.value })}
                            />
                        </Box>
                        <TextField
                            label="Base URL"
                            fullWidth
                            size="small"
                            value={currentAccount.baseUrl}
                            onChange={(e) => setCurrentAccount({ ...currentAccount, baseUrl: e.target.value })}
                        />
                        <TextField
                            label={isEditingAccount ? 'API Key（留空则不更换）' : 'API Key'}
                            fullWidth
                            size="small"
                            type="password"
                            value={currentAccount.apiKey}
                            onChange={(e) => setCurrentAccount({ ...currentAccount, apiKey: e.target.value })}
                            autoComplete="new-password"
                        />
                        <TextField
                            label="模型"
                            fullWidth
                            size="small"
                            value={currentAccount.model}
                            onChange={(e) => setCurrentAccount({ ...currentAccount, model: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="优先级（越小越优先）"
                                fullWidth
                                size="small"
                                type="number"
                                inputProps={{ min: 1, step: 1 }}
                                value={currentAccount.priority}
                                onChange={(e) => setCurrentAccount({ ...currentAccount, priority: e.target.value })}
                            />
                            <TextField
                                label="权重（≥ 1）"
                                fullWidth
                                size="small"
                                type="number"
                                inputProps={{ min: 1, step: 1 }}
                                value={currentAccount.weight}
                                onChange={(e) => setCurrentAccount({ ...currentAccount, weight: e.target.value })}
                            />
                        </Box>
                        <TextField
                            label="备注"
                            fullWidth
                            size="small"
                            value={currentAccount.remark}
                            onChange={(e) => setCurrentAccount({ ...currentAccount, remark: e.target.value })}
                        />
                        {accountSaveMsg.msg && (
                            <Alert severity={accountSaveMsg.type} sx={{ borderRadius: 1 }}>{accountSaveMsg.msg}</Alert>
                        )}
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setAccountDialogOpen(false)} color="inherit" disabled={accountSaving}>取消</Button>
                    <Button onClick={handleSaveAccount} variant="contained" disabled={accountSaving} startIcon={accountSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
                        {accountSaving ? '保存中...' : '保存'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 弹窗：AI Key 删除确认 */}
            <Dialog open={deleteAccountDialogOpen} onClose={() => setDeleteAccountDialogOpen(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle>确认删除账号</DialogTitle>
                <DialogContent>
                    <Typography>确定要删除账号「<strong>{accountToDelete?.name}</strong>」吗？历史日志将保留，但账号会立即移出调度池。此操作不可撤销。</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteAccountDialogOpen(false)} color="inherit">取消</Button>
                    <Button onClick={handleConfirmDeleteAccount} color="error" variant="contained" disableElevation>确认删除</Button>
                </DialogActions>
            </Dialog>

            {/* ══════════════════════ 弹窗：调用日志 ══════════════════════ */}
            <Dialog
                open={logsDrawerOpen}
                onClose={() => setLogsDrawerOpen(false)}
                fullWidth
                maxWidth="xl"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">调用日志</Typography>
                        {logsAccount && (
                            <Typography variant="body2" color="text.secondary">账号：{logsAccount.name}（ID: {logsAccount.id}）</Typography>
                        )}
                    </Box>
                    <IconButton onClick={() => setLogsDrawerOpen(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        {logsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                        ) : (
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>状态</TableCell>
                                        <TableCell>HTTP</TableCell>
                                        <TableCell>错误码</TableCell>
                                        <TableCell>错误信息</TableCell>
                                        <TableCell>耗时(ms)</TableCell>
                                        <TableCell>Prompt Tokens</TableCell>
                                        <TableCell>Completion Tokens</TableCell>
                                        <TableCell>时间</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>暂无日志</TableCell>
                                        </TableRow>
                                    ) : logs.map((log) => (
                                        <TableRow key={log.id} hover>
                                            <TableCell>
                                                <Chip
                                                    label={log.status === 'success' ? '成功' : '失败'}
                                                    color={log.status === 'success' ? 'success' : 'error'}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 1 }}
                                                />
                                            </TableCell>
                                            <TableCell>{log.httpStatus || '-'}</TableCell>
                                            <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <Tooltip title={log.errorCode || ''}><span>{log.errorCode || '-'}</span></Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <Tooltip title={log.errorMessage || ''}><span>{log.errorMessage || '-'}</span></Tooltip>
                                            </TableCell>
                                            <TableCell>{log.durationMs ?? '-'}</TableCell>
                                            <TableCell>{log.promptTokens ?? '-'}</TableCell>
                                            <TableCell>{log.completionTokens ?? '-'}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {formatDateTime(log.createdAt)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 2, py: 1, justifyContent: 'space-between' }}>
                    <TablePagination
                        component="div"
                        count={-1}
                        page={logsPage}
                        rowsPerPage={logsRowsPerPage}
                        onPageChange={handleLogsPageChange}
                        onRowsPerPageChange={handleLogsRowsPerPageChange}
                        rowsPerPageOptions={[10, 20, 50]}
                        labelRowsPerPage="每页条数"
                        labelDisplayedRows={({ from, to }) => `${from}-${to}`}
                        sx={{ borderTop: 'none' }}
                    />
                    <Button onClick={() => setLogsDrawerOpen(false)} color="inherit">关闭</Button>
                </DialogActions>
            </Dialog>

            {/* ══════════════════════ 弹窗：聚合统计 ══════════════════════ */}
            <Dialog
                open={statsDrawerOpen}
                onClose={() => setStatsDrawerOpen(false)}
                fullWidth
                maxWidth="xl"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">AI Key 聚合统计</Typography>
                    <IconButton onClick={() => setStatsDrawerOpen(false)} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 0 }}>
                    {statsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : (
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>账号</TableCell>
                                    <TableCell>Provider</TableCell>
                                    <TableCell>Scene</TableCell>
                                    <TableCell align="right">总请求</TableCell>
                                    <TableCell align="right">成功</TableCell>
                                    <TableCell align="right">失败</TableCell>
                                    <TableCell align="right">Prompt Tokens</TableCell>
                                    <TableCell align="right">Completion Tokens</TableCell>
                                    <TableCell align="right">Reasoning Tokens</TableCell>
                                    <TableCell>最近请求</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>暂无统计数据</TableCell>
                                    </TableRow>
                                ) : stats.map((s) => (
                                    <TableRow key={s.accountId} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>{s.accountName}</TableCell>
                                        <TableCell>{s.provider}</TableCell>
                                        <TableCell>{s.scene}</TableCell>
                                        <TableCell align="right">{s.totalRequests}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main' }}>{s.successRequests}</TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main' }}>{s.failedRequests}</TableCell>
                                        <TableCell align="right">{s.totalPromptTokens?.toLocaleString()}</TableCell>
                                        <TableCell align="right">{s.totalCompletionTokens?.toLocaleString()}</TableCell>
                                        <TableCell align="right">{s.totalReasoningTokens?.toLocaleString()}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {formatDateTime(s.lastRequestAt)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button onClick={() => setStatsDrawerOpen(false)} color="inherit">关闭</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SummaryPage;
